import { Component } from "react"

/* Redux */
import { connect } from "react-redux"

/* Components */
import { InnerLayout, Pagination } from "../../components"

/* Widgets */
import { Tabs, RsgReport } from "../../widgets/Rsg"

/* REST API */
import { list } from "../../api/Rsg"

/* Helpers */
import { withRouter } from "../../helpers"

/* Icons */
import { SearchIcon, CloseIcon, EyeIcon, DownloadIcon, ReportIcon, GameIcon } from "../../widgets/Rsg/RsgIcons"

/* Utils */
import { formatSigned, formatDate, formatPeriod, downloadReport } from "../../widgets/Rsg/RsgUtils"

/* Translate */
import { t } from "../../translates/translate"


/* DELAY BEFORE THE SEARCH REQUEST LEAVES */
const DELAY = 400

/* REPORT TYPES OF THE REGULATOR — AN EMPTY VALUE KEEPS EVERY TYPE */
const TYPES = ["", "COMPLIANCE", "OPERATION"]


/* Page RSG Reports */
class RsgReports extends Component {

    constructor() {
        super()

        this.state = {
            data: [],
            status: "loading",
            count: 0,
            search: "",
            type: "",
            downloading: 0,
        }
    }

    componentDidMount = () => {
        window.scrollTo(0, 0)
        this.load()
    }

    componentDidUpdate = (prevProps) => {

        /* Fields */
        const { params } = this.props
        const { page } = params

        /* Check Page — a new page starts from the top with the loader */
        if (prevProps.params.page !== page) {
            window.scrollTo(0, 0)
            this.load(true)
        }

    }

    componentWillUnmount = () => {
        clearTimeout(this._timer)
    }

    /* Get Page */
    getPage = () => {

        /* Fields */
        const { params } = this.props
        const { page } = params

        /* Check Page */
        if (Number.isInteger(parseInt(page))) {
            return parseInt(page)
        }

        return 1
    }

    /* Load Action */
    load = (reload = false) => {

        /* Fields */
        const { search, type } = this.state
        const page = this.getPage()

        /* Check Reload */
        if (reload) {
            this.setState({ status: "loading" })
        }

        /* Send to REST API */
        list(page, search, type).then((response) => {
            if (response.status === 200) {
                this.setState({ status: "ok", data: response.data.list, count: response.data.count })
            }
            else if (response.status === 401 || response.status === 403) {
                this.setState({ status: "permission", data: [], count: 0 })
            }
            else if (response.status === 498) {
                this.setState({ status: "key", data: [], count: 0 })
            }
            else {
                this.setState({ status: "error", data: [], count: 0 })
            }
        }).catch(() => {
            this.setState({ status: "network", data: [], count: 0 })
        })

    }

    /* Change Search — the request leaves only after the typing stops */
    changeSearch = (search) => {

        this.setState({ search })

        clearTimeout(this._timer)
        this._timer = setTimeout(() => this.apply(), DELAY)
    }

    /* Change Type */
    changeType = (type) => {
        this.setState({ type }, () => this.apply())
    }

    /* Apply Action — every filter restarts from the first page */
    apply = () => {

        /* Fields */
        const { navigate } = this.props

        /* Check Page */
        if (this.getPage() !== 1) {
            navigate("/rsg/1")
            return
        }

        this.load()
    }

    /* Download Action */
    download = (item) => {

        /* Fields */
        const { downloading } = this.state

        /* Check Downloading */
        if (downloading === item.id) {
            return
        }

        /* Start Downloading */
        this.setState({ downloading: item.id })

        downloadReport(item).finally(() => {
            this.setState({ downloading: 0 })
        })

    }

    /* Draw Search */
    _search = () => {

        /* Fields */
        const { search } = this.state

        return (
            <div className="rsg--search">

                <SearchIcon />

                <input
                    value={search}
                    onChange={(event) => this.changeSearch(event.target.value)}
                    placeholder={t("rsg.searchByFile")}
                />

                {search !== "" &&
                    <div onClick={() => this.changeSearch("")} className="rsg--search-clear">
                        <CloseIcon size={12} />
                    </div>
                }

            </div>
        )
    }

    /* Draw Type Filter */
    _filter = () => {

        /* Fields */
        const { type } = this.state

        return (
            <div className="rsg--filter">
                {TYPES.map((item, index) =>
                    <div
                        onClick={() => item !== type && this.changeType(item)}
                        className={`rsg--filter-item ${item === type ? "active" : ""}`}
                        key={`${index}`}
                    >
                        {item === "" ? t("rsg.allTypes") : item}
                    </div>
                )}
            </div>
        )
    }

    /* Draw Actions */
    _actions = (item) => {

        /* Fields */
        const { downloading } = this.state

        return (
            <>
                <div onClick={() => this._report.open({ id: item.id })} className="rsg--action" title={t("rsg.detailTitle")}>
                    <EyeIcon />
                </div>

                {item.fileExists
                    ? <div onClick={() => this.download(item)} className="rsg--action" title={t("rsg.fileDownload")}>
                        {downloading === item.id ? <div className="rsg--loading tiny" /> : <DownloadIcon size={16} />}
                    </div>
                    : <div className="rsg--action off" title={t("rsg.fileMissing")}>
                        <DownloadIcon size={16} />
                    </div>
                }
            </>
        )
    }

    /* Draw Content */
    _content = () => {

        /* Fields */
        const { status, data, count, search } = this.state
        const { navigate } = this.props
        const page = this.getPage()

        /* Check Status */
        if (status !== "ok") {
            return (
                <div className="rsg--page">
                    <InnerLayout inner status={status} reload={() => this.load(true)} />
                </div>
            )
        }

        return (
            <div className="rsg--page">

                {/* Head */}
                <div className="rsg--table-header">
                    <div className="rsg--column number">#</div>
                    <div className="rsg--column file">{t("rsg.fileName")}</div>
                    <div className="rsg--column period">{t("rsg.reportDate")}</div>
                    <div className="rsg--column ggr">{t("rsg.ggr")}</div>
                    <div className="rsg--column games">{t("rsg.games")}</div>
                    <div className="rsg--column status">{t("rsg.status")}</div>
                    <div className="rsg--column actions" />
                </div>

                {/* Empty */}
                {data.length === 0 &&
                    <div className="rsg--empty">
                        <ReportIcon size={40} />
                        <span>{search !== "" ? t("rsg.nothingFound") : t("rsg.reportsEmpty")}</span>
                    </div>
                }

                {/* Body */}
                {data.map((item, index) =>
                    <div onClick={() => this._report.open({ id: item.id })} className="rsg--row" key={`${index}`}>

                        <div className="rsg--column number">{(page - 1) * 10 + index + 1}</div>

                        <div className="rsg--column file">

                            <div className={`rsg--file-icon ${item.reportType === "OPERATION" ? "orange" : ""}`}>
                                <ReportIcon size={18} />
                            </div>

                            <div className="rsg--file-text">
                                <div className="rsg--file-name">{item.fileName}</div>
                                <div className="rsg--file-type">
                                    {item.reportType}
                                    {item.fileRegenerated && <span className="rsg--file-new">{t("rsg.fileRegenerated")}</span>}
                                </div>
                            </div>

                        </div>

                        <div className="rsg--column period">
                            <div className="rsg--period">{formatPeriod(item.reportDate, item.reportType)}</div>
                            <div className="rsg--period-note">{formatDate(item.uploadDate || item.createdAt)}</div>
                        </div>

                        <div className="rsg--column ggr">
                            <div className={`rsg--amount ${parseFloat(item.ggrUSD) >= 0 ? "in" : "out"}`}>{formatSigned(item.ggrUSD)} $</div>
                            <div className={`rsg--amount ${parseFloat(item.ggr) >= 0 ? "in" : "out"}`}>{formatSigned(item.ggr)} ₾</div>
                        </div>

                        <div className="rsg--column games">
                            <div className={`rsg--games ${parseInt(item.gamecount) === 0 ? "empty" : ""}`}>
                                <GameIcon />
                                <span>{item.gamecount}</span>
                            </div>
                        </div>

                        <div className="rsg--column status">
                            <div className={`rsg--status ${parseInt(item.uploaded) === 1 ? "green" : "red"}`}>
                                {parseInt(item.uploaded) === 1 ? t("rsg.uploaded") : t("rsg.notUploaded")}
                            </div>
                        </div>

                        <div onClick={(event) => event.stopPropagation()} className="rsg--column actions">{this._actions(item)}</div>

                    </div>
                )}

                {/* Pagination */}
                <Pagination size={10} page={page} total={count} onPageChange={(page) => navigate(`/rsg/${page}`)} />

            </div>
        )
    }

    render = () => {

        /* Fields */
        const { status } = this.state

        return (
            <div className="page">

                <div className="page-header rsg--header">

                    <h1>{t("rsg.title")}</h1>

                    {status === "ok" &&
                        <div className="rsg--tools">

                            {/* Draw Search */}
                            {this._search()}

                            {/* Draw Type Filter */}
                            {this._filter()}

                        </div>
                    }

                </div>

                {/* Tabs */}
                <Tabs active="reports" />

                {/* Draw Content */}
                {this._content()}

                {/* Draw Report Modal */}
                <RsgReport ref={(ref) => this._report = ref} />

            </div>
        )
    }

}

const mapStateToProps = (state) => ({
    admin: state.admin,
    language: state.language,
})

export default connect(mapStateToProps)(withRouter(RsgReports))
